import {createFormHook} from '@tanstack/react-form';
import {useMemo} from 'react';
import {z} from 'zod';

import {FormCheckbox} from '../components/Checkbox';
import {FormSingleFileInput} from '../components/FileInput';
import {FormTextareaInput} from '../components/TextareaInput';
import {FormMultiTextInput, FormTextInput} from '../components/TextInput';
import {fieldContext, formContext} from '../utils/formContext';
import {zodTanstackValidate} from '../utils/zodTanstackValidate';

const VALIDATOR_KEYS = [
  'onMount',
  'onChange',
  'onChangeAsync',
  'onBlur',
  'onBlurAsync',
  'onSubmit',
  'onSubmitAsync',
  'onDynamic',
  'onDynamicAsync',
] as const;

/**
 * TanStack Form detects a Zod schema via its Standard Schema interface
 * (`schema['~standard']`) and calls `validate(value)` with no way to pass a
 * contextual error map through — so any raw Zod schema found here is swapped
 * for `zodTanstackValidate(schema)`, which resolves messages through
 * `formErrorMap` instead of Zod's stock defaults. Applied centrally (here,
 * plus `AppField`/`Field` below) so every schema handed to `validators`
 * anywhere in the app gets friendly messages automatically, with nothing for
 * individual forms to remember to do.
 */
function withFriendlyZodErrors<T extends Record<string, unknown>>(
  validators: T
): T {
  const wrapped: Record<string, unknown> = {...validators};
  for (const key of VALIDATOR_KEYS) {
    const validator = wrapped[key];
    if (validator instanceof z.ZodType) {
      wrapped[key] = zodTanstackValidate(validator);
    }
  }
  return wrapped as T;
}

const {useAppForm: useBaseAppForm, withForm} = createFormHook({
  fieldComponents: {
    FormTextInput,
    FormTextareaInput,
    FormSingleFileInput,
    FormMultiTextInput,
    FormCheckbox,
  },
  formComponents: {
    // SubscribeButton,
  },
  fieldContext,
  formContext,
});

export {withForm};

export const useAppForm: typeof useBaseAppForm = ((props: {
  validators?: Record<string, unknown>;
}) => {
  const form = useBaseAppForm({
    ...props,
    validators: props.validators && withFriendlyZodErrors(props.validators),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any);

  return useMemo(() => {
    const AppField = form.AppField;
    const Field = form.Field;

    return Object.assign(form, {
      AppField: (fieldProps: {validators?: Record<string, unknown>}) => (
        <AppField
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          {...(fieldProps as any)}
          validators={
            fieldProps.validators &&
            withFriendlyZodErrors(fieldProps.validators)
          }
        />
      ),
      Field: (fieldProps: {validators?: Record<string, unknown>}) => (
        <Field
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          {...(fieldProps as any)}
          validators={
            fieldProps.validators &&
            withFriendlyZodErrors(fieldProps.validators)
          }
        />
      ),
    });
  }, [form]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}) as any;
